// src/pages/finance/Expenses.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormHelperText,
  Alert,
  Snackbar,
  Drawer
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarExport
} from '@mui/x-data-grid';
import {
  TrendingDown as ExpenseIcon,
  ReceiptLong as ReceiptIcon,
  CalendarMonth as DateIcon,
  Store as SupplierIcon,
  Description as DescIcon,
  AddCircle as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  LocalPostOffice as DocIcon,
  CheckCircle as CheckedIcon,
  Warning as PendingIcon
} from '@mui/icons-material';

const colors = {
  grey: '#64748b',
};

const StatCard = ({ title, value, icon: Icon, color, subtitle, delay = 0 }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay * 100}ms` }}>
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.03)',
        borderLeft: `4px solid ${color}`,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px 0 rgba(31, 38, 135, 0.08)',
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" sx={{ color: colors.grey, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.68rem' }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, color: '#1e293b', fontSize: '1.25rem' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: colors.grey, display: 'block', marginTop: '2px', fontWeight: 500, fontSize: '0.7rem' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${color}15`,
              color: color,
              '& svg': { fontSize: 20 },
              flexShrink: 0
            }}
          >
            {Icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Zoom>
);

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  
  // Modales
  const [openModal, setOpenModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Visor de Comprobantes
  const [activeVoucherUrl, setActiveVoucherUrl] = useState(null);
  const [activeVoucherTitle, setActiveVoucherTitle] = useState('');
  
  // Notificaciones Toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  
  // Filtros
  const [filterMonth, setFilterMonth] = useState('');
  
  // Estado de formulario
  const [form, setForm] = useState({
    category_id: '',
    account_id: '',
    amount: '',
    expense_date: new Date().toISOString().substring(0, 10),
    supplier: '',
    description: '',
    document_type: 'ninguno',
    document_number: '',
    status: 'paid'
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [expensesRes, catsRes, accountsRes] = await Promise.all([
        fetch('/api/expenses', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/accounting/categories', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/accounting/accounts', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (expensesRes.ok && catsRes.ok && accountsRes.ok) {
        const expensesData = await expensesRes.json();
        const catsData = await catsRes.json();
        const accountsData = await accountsRes.json();

        setExpenses(expensesData);
        setCategories(catsData.filter(c => c.type === 'expense'));
        setAccounts(accountsData.filter(a => a.status === 'active'));
      } else {
        showToast('Error al cargar datos del servidor', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error de conexión con el servidor', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingExpense(null);
    setForm({
      category_id: categories.length > 0 ? categories[0].id : '',
      account_id: accounts.length > 0 ? accounts[0].id : '',
      amount: '',
      expense_date: new Date().toISOString().substring(0, 10),
      supplier: '',
      description: '',
      document_type: 'ninguno',
      document_number: '',
      status: 'paid'
    });
    setSelectedFile(null);
    setOpenModal(true);
  };

  const handleOpenEdit = (expense) => {
    setEditingExpense(expense);
    setForm({
      category_id: expense.category_id || '',
      account_id: expense.account_id || '',
      amount: expense.amount.toString(),
      expense_date: new Date(expense.expense_date).toISOString().substring(0, 10),
      supplier: expense.supplier || '',
      description: expense.description || '',
      document_type: expense.document_type || 'ninguno',
      document_number: expense.document_number || '',
      status: expense.status || 'paid'
    });
    setSelectedFile(null);
    setOpenModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('El comprobante supera el límite permitido de 5MB', 'warning');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Adjuntar campos del formulario
      Object.entries(form).forEach(([key, val]) => {
        formData.append(key, val);
      });
      
      // Adjuntar archivo si existe
      if (selectedFile) {
        formData.append('receipt', selectedFile);
      }

      const url = editingExpense ? `/api/expenses/${editingExpense.id}` : '/api/expenses';
      const method = editingExpense ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        showToast(editingExpense ? 'Gasto actualizado con éxito' : 'Gasto registrado con éxito', 'success');
        setOpenModal(false);
        fetchInitialData();
      } else {
        const data = await response.json();
        showToast(data.message || 'Error al guardar el gasto', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error de red al registrar el gasto', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el gasto a "${expense.supplier || 'Proveedor'}" por S/ ${expense.amount.toFixed(2)}?\nEsto devolverá los fondos a la cuenta correspondiente.`)) {
      return;
    }

    setActionInProgress(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/expenses/${expense.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showToast('Gasto eliminado exitosamente y fondos restaurados.', 'success');
        fetchInitialData();
      } else {
        const data = await response.json();
        showToast(data.message || 'Error al eliminar el gasto', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast('Error al conectar con el servidor', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const getVoucherTypeChip = (type) => {
    switch (type) {
      case 'factura':
        return <Chip label="Factura" size="small" variant="outlined" sx={{ color: '#0ea5e9', borderColor: '#0ea5e9', fontWeight: 600, borderRadius: '6px' }} />;
      case 'boleta':
        return <Chip label="Boleta" size="small" variant="outlined" sx={{ color: '#8b5cf6', borderColor: '#8b5cf6', fontWeight: 600, borderRadius: '6px' }} />;
      case 'recibo':
        return <Chip label="Recibo" size="small" variant="outlined" sx={{ color: '#10b981', borderColor: '#10b981', fontWeight: 600, borderRadius: '6px' }} />;
      case 'voucher':
        return <Chip label="Voucher" size="small" variant="outlined" sx={{ color: '#f59e0b', borderColor: '#f59e0b', fontWeight: 600, borderRadius: '6px' }} />;
      default:
        return <Chip label="Ninguno" size="small" sx={{ borderRadius: '6px', color: '#64748b' }} />;
    }
  };

  const getStatusChip = (status) => {
    if (status === 'paid') {
      return (
        <Chip
          label="Pagado"
          size="small"
          icon={<CheckedIcon style={{ color: '#059669', fontSize: '0.9rem' }} />}
          sx={{
            background: 'rgba(16, 185, 129, 0.08)',
            color: '#059669',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            fontWeight: 600,
            borderRadius: '8px',
          }}
        />
      );
    }
    return (
      <Chip
        label="Por Pagar"
        size="small"
        icon={<PendingIcon style={{ color: '#d97706', fontSize: '0.9rem' }} />}
        sx={{
          background: 'rgba(245, 158, 11, 0.08)',
          color: '#d97706',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          fontWeight: 600,
          borderRadius: '8px',
        }}
      />
    );
  };

  const handleViewVoucher = (expense) => {
    setActiveVoucherTitle(`Comprobante: ${expense.supplier || 'Gasto'} - S/ ${expense.amount.toFixed(2)}`);
    setActiveVoucherUrl(expense.receipt_image_url);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
      <GridToolbarExport sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, color: '#64748b', alignSelf: 'flex-start' }} />
      <GridToolbarQuickFilter
        sx={{
          width: { xs: '100%', sm: 'auto' },
          '& .MuiInputBase-root': {
            borderRadius: '12px',
            backgroundColor: '#f8fafc',
            px: 1.5,
            py: 0.2,
            border: '1px solid rgba(0,0,0,0.06)',
            '&:before, &:after': { display: 'none' },
            width: '100%'
          }
        }}
      />
    </GridToolbarContainer>
  );

  const columns = [
    {
      field: 'expense_date',
      headerName: 'Fecha',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
          {new Date(params.value).toLocaleDateString('es-PE', { timeZone: 'UTC' })}
        </Typography>
      )
    },
    {
      field: 'category_name',
      headerName: 'Rubro / Categoría',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
          {params.value || 'Otros Egresos'}
        </Typography>
      )
    },
    {
      field: 'supplier',
      headerName: 'Proveedor / Destino',
      width: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <SupplierIcon sx={{ color: '#94a3b8', fontSize: '1.1rem' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
            {params.value || 'Gasto General'}
          </Typography>
        </Stack>
      )
    },
    {
      field: 'account_name',
      headerName: 'Caja / Banco',
      width: 180,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Caja Chica'}
          size="small"
          sx={{
            borderRadius: '6px',
            backgroundColor: '#f1f5f9',
            color: '#475569',
            fontWeight: 600,
            fontSize: '0.75rem'
          }}
        />
      )
    },
    {
      field: 'document_type',
      headerName: 'Comprobante',
      width: 140,
      renderCell: (params) => getVoucherTypeChip(params.value)
    },
    {
      field: 'document_number',
      headerName: 'N° Documento',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#64748b', fontWeight: 600 }}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 130,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 130,
      type: 'number',
      renderCell: (params) => {
        const val = params.value || 0;
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 800, color: '#ef4444' }}>
            S/ {val.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          {params.row.receipt_image_url && (
            <Tooltip title="Ver Comprobante">
              <IconButton
                size="small"
                onClick={() => handleViewVoucher(params.row)}
                sx={{
                  color: '#0ea5e9',
                  backgroundColor: 'rgba(14, 165, 233, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(14, 165, 233, 0.15)' }
                }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => handleOpenEdit(params.row)}
              sx={{
                color: '#6366f1',
                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.15)' }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row)}
              disabled={actionInProgress}
              sx={{
                color: '#ef4444',
                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.15)' }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  // Cálculos rápidos para KPIs
  const totalSpent = expenses
    .filter(e => e.status === 'paid')
    .reduce((acc, exp) => acc + (exp.amount || 0), 0);
  
  const totalPending = expenses
    .filter(e => e.status === 'pending')
    .reduce((acc, exp) => acc + (exp.amount || 0), 0);

  // Rubro con mayor costo
  const catSums = expenses
    .filter(e => e.status === 'paid')
    .reduce((acc, exp) => {
      const cat = exp.category_name || 'Otros Egresos';
      acc[cat] = (acc[cat] || 0) + exp.amount;
      return acc;
    }, {});
  let highestCategory = 'Ninguno';
  let highestAmount = 0;
  Object.entries(catSums).forEach(([cat, sum]) => {
    if (sum > highestAmount) {
      highestAmount = sum;
      highestCategory = cat;
    }
  });

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 1, sm: 1.5, md: 2 } }}>
      {/* Cabecera de Egresos */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyItems="center" justifyContent="space-between" sx={{ mb: 2 }} gap={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Gastos y Egresos WISP 💸
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 0.2, fontWeight: 500, display: 'block' }}>
            Registra y audita las salidas de caja chica, pagos a proveedores, y costos de infraestructura de red de tu ISP.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          sx={{
            alignSelf: { xs: 'stretch', sm: 'center' },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 'bold',
            px: 2,
            height: '36px',
            fontSize: '0.875rem',
            backgroundColor: '#ef4444',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#dc2626' }
          }}
        >
          Registrar Gasto
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Gastado (Egresos)"
            value={`S/ ${totalSpent.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<ExpenseIcon />}
            color="#ef4444"
            subtitle="Pagos ejecutados en total"
            delay={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Gastos Por Pagar (Pendientes)"
            value={`S/ ${totalPending.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<PendingIcon />}
            color="#f59e0b"
            subtitle="Cuentas y facturas de proveedores por pagar"
            delay={1}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Rubro con Mayor Gasto"
            value={highestCategory}
            icon={<ReceiptIcon />}
            color="#6366f1"
            subtitle={highestAmount > 0 ? `Total: S/ ${highestAmount.toFixed(2)}` : 'Sin gastos registrados'}
            delay={2}
          />
        </Grid>
      </Grid>

      {/* Tabla de Datos */}
      <Paper
        sx={{
          borderRadius: '24px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.05)',
          overflow: 'hidden',
          backgroundColor: 'white',
          p: 1
        }}
      >
        <DataGrid
          rows={expenses}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableSelectionOnClick
          loading={loading}
          autoHeight
          getRowId={(row) => row.id}
          components={{
            Toolbar: CustomToolbar
          }}
          sx={{
            border: 'none',
            fontFamily: 'Inter, sans-serif',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: '#475569',
                fontSize: '0.85rem'
              }
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(0,0,0,0.04)',
              color: '#334155',
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                transform: 'scale(1.001)',
              }
            }
          }}
        />
      </Paper>

      {/* DIÁLOGO MODAL: AGREGAR / EDITAR GASTO */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #e2e8f0', p: 3 }}>
          {editingExpense ? '✏️ Editar Registro de Gasto' : '💸 Registrar Nuevo Gasto'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            
            <Grid container spacing={2}>
              {/* Categoría */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Categoría / Rubro"
                  name="category_id"
                  value={form.category_id}
                  onChange={handleFormChange}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Cuenta Financiera */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Cuenta de Pago / Caja"
                  name="account_id"
                  value={form.account_id}
                  onChange={handleFormChange}
                  helperText="Cuenta de la cual se descontarán los fondos."
                >
                  {accounts.map(acc => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.name} (S/ {acc.current_balance.toFixed(2)})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Monto */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Monto del Gasto"
                  name="amount"
                  type="number"
                  value={form.amount}
                  onChange={handleFormChange}
                  inputProps={{ step: '0.01', min: '0.01' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">S/</InputAdornment>
                  }}
                />
              </Grid>

              {/* Fecha */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Fecha de Gasto"
                  name="expense_date"
                  value={form.expense_date}
                  onChange={handleFormChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Proveedor */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Proveedor / Destinatario"
                  name="supplier"
                  value={form.supplier}
                  onChange={handleFormChange}
                  placeholder="Ej: Claro Perú, Luz del Sur, Empresa Electrónica"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SupplierIcon color="action" fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              {/* Descripción */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Descripción / Detalles"
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Detalles sobre el egreso realizado..."
                />
              </Grid>

              {/* Tipo de Documento */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de Comprobante"
                  name="document_type"
                  value={form.document_type}
                  onChange={handleFormChange}
                >
                  <MenuItem value="ninguno">Ninguno / Voucher Simple</MenuItem>
                  <MenuItem value="factura">Factura</MenuItem>
                  <MenuItem value="boleta">Boleta de Venta</MenuItem>
                  <MenuItem value="recibo">Recibo por Honorarios / Servicio</MenuItem>
                  <MenuItem value="voucher">Voucher Bancario</MenuItem>
                </TextField>
              </Grid>

              {/* Número de Documento */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="N° de Comprobante"
                  name="document_number"
                  value={form.document_number}
                  onChange={handleFormChange}
                  placeholder="Ej: F001-0004523"
                />
              </Grid>

              {/* Estado */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Estado del Pago"
                  name="status"
                  value={form.status}
                  onChange={handleFormChange}
                  helperText="Pendiente no afecta los balances hasta marcarse como Pagado."
                >
                  <MenuItem value="paid">Pagado (Conciliado)</MenuItem>
                  <MenuItem value="pending">Por Pagar (Deuda Pendiente)</MenuItem>
                </TextField>
              </Grid>

              {/* Subida de Archivo */}
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<UploadIcon />}
                  sx={{ height: '56px', borderRadius: 2, textTransform: 'none', borderStyle: 'dashed' }}
                >
                  {selectedFile ? 'Cambiar Comprobante' : 'Subir Comprobante'}
                  <input
                    type="file"
                    hidden
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFile && (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5, fontWeight: 'bold' }}>
                    📎 {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                )}
              </Grid>
            </Grid>

          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0', gap: 1.5 }}>
            <Button onClick={() => setOpenModal(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                backgroundColor: '#ef4444',
                '&:hover': { backgroundColor: '#dc2626' }
              }}
            >
              {saving ? 'Guardando...' : 'Guardar Gasto'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DRAWER VISOR DE COMPROBANTES DE FONDO */}
      <Drawer
        anchor="right"
        open={!!activeVoucherUrl}
        onClose={() => setActiveVoucherUrl(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, p: 3, display: 'flex', flexDirection: 'column' } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, pb: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="h6" fontWeight="bold">
            {activeVoucherTitle}
          </Typography>
          <IconButton onClick={() => setActiveVoucherUrl(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        {activeVoucherUrl && (
          <Box flexGrow={1} display="flex" justifyContent="center" alignItems="center" sx={{ bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            {activeVoucherUrl.endsWith('.pdf') || activeVoucherUrl.includes('/pdf') ? (
              <iframe
                src={activeVoucherUrl}
                title="Visor PDF de Comprobante"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
              />
            ) : (
              <img
                src={activeVoucherUrl}
                alt="Imagen de Comprobante de Gasto WISP"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            )}
          </Box>
        )}
      </Drawer>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Expenses;
